import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import {
  SortDescriptor,
  process,
  State,
  GroupDescriptor,
  CompositeFilterDescriptor,
} from '@progress/kendo-data-query';
import { SessionStorageService } from '../../services/session-storage.service';
import { UniversalSearchService } from '../services/universal-search.service';
import { HierarchyModalComponent } from '../hierarchy-modal/hierarchy-modal.component';
import { SearchModalComponent } from '../search-modal/search-modal.component';
import { SuggestionEngineService } from '../../suggestion-engine/suggestion-engine.service';
import { SharedReportDialogComponent } from '../../shared-components/report-dialog/report-dialog.component';
import { RightBridgeApiService } from '../../services/right-bridge-api.service';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { saveAs } from '@progress/kendo-file-saver';
import { Platform } from '@angular/cdk/platform';
import { EnvironmentService } from '../../services/environment.service';
import { DateTime } from 'luxon';

import { BreadcrumbComponent } from '../../breadcrumb/breadcrumb.component';
import { NgxLoadingModule } from 'ngx-loading';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';

@Component({
  selector: 'app-universal-search',
  templateUrl: './universal-search.component.html',
  styleUrls: ['./universal-search.component.scss'],
  imports: [
    BreadcrumbComponent,
    NgxLoadingModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    KENDO_GRID,
    FormsModule,
    CommonModule,
    MatSelectModule,
    MatTooltipModule,
  ],
  providers: [provideLuxonDateAdapter()],
})
export class UniversalSearchComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private usService = inject(UniversalSearchService);
  private ss = inject(SessionStorageService);
  private rbs = inject(RightBridgeApiService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private ses = inject(SuggestionEngineService);
  private params = inject(ActivatedRoute);
  private pltfrm = inject(Platform);
  private envSvc = inject(EnvironmentService);

  private environment;
  private baseUrl: string;

  public gridData: any;
  public ogData = { data: [], total: 0 };
  public gridColumns: any[] = [];
  public pageSize = 10;
  public pageableData = {
    buttonCount: 3,
    refresh: true,
    alwaysVisible: false,
    pageSizes: [this.pageSize, this.pageSize * 2.5, this.pageSize * 5],
  };
  public skip = 0;
  public sort: SortDescriptor[] = [];
  public filter: CompositeFilterDescriptor = { logic: 'and', filters: [] };
  public group: GroupDescriptor[] = [];
  public hidden: string[] = [];
  public state: State = {
    skip: this.skip,
    take: this.pageSize,
  };
  current = 'search';
  loading = false;
  currentUser: string;
  rights;

  selectedUser;
  selectedUnit;
  selectedRepCode;
  selectedProfiles = [];

  userSearch;
  unitSearch;
  repCodeSearch;
  searchType;
  suggestions;
  searchModel;
  bulkAction;
  header = 'Case Search';
  hideTiles = false;
  allSelected = false;

  passedInSearch = false;

  startRow = 1;
  endRow = 99999;

  savedSearch;

  constructor() {
    this.exportExcelData = this.exportExcelData.bind(this);
  }

  ngOnInit() {
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;

    this.rights = this.ss.get('rights');
    const globals = this.ss.get('globals');
    this.currentUser = globals?.user.id;
    if (this.ss.get('proxySession')) {
      this.currentUser = this.ss.get('proxySession').userID;
    }
    this.hidden = this.ss.get('hiddenSearchColumns') || [];
    this.savedSearch = this.ss.get('searchStorage');

    this.header =
      this.ss.get('currentApp') === 'se' ? 'Client Search' : this.header;

    if (this.savedSearch) {
      this.reloadSearch();
    }
  }

  ngOnDestroy(): void {
    this.ss.remove('searchStorage');
  }

  ngAfterViewInit() {
    this.params.params.subscribe(pr => {
      if (pr && pr.type && pr.type === 'suggestion') {
        if (pr && pr.type && pr.id) {
          this.passedInSearch = true;
          this.searchType = pr.type;
          const saved = `{"search": [{"searchBy": "profileBridge"}, {"userid": "${this.currentUser}"}, {"bridgeids": "${pr.id}"}, {"score": 500}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;

          const searchStorage = { type: this.searchType, searchDef: saved };
          this.ss.set('searchStorage', searchStorage);
          this.search(saved);
        }
      } else if (pr && pr.type && pr.type === 'callBack') {
        this.passedInSearch = true;
        this.searchType = pr.type;
        const start = pr.id.split('~')[0].replace('-', '/');
        const end = pr.id.split('~')[1].replace('-', '/');
        const saved = `{"search": [{Prospects: true}, {"userid": "${this.currentUser}"}, {"searchby": "callBackDate"},  {"StartDate": "${start}"}, {"EndDate": "${end}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
        const searchStorage = { type: this.searchType, searchDef: saved };

        this.ss.set('searchStorage', searchStorage);
        this.search(saved);
      } else if (pr && pr.type && pr.type === 'lastAction30') {
        const date = DateTime.now().minus({ days: 30 }).toFormat('LL/dd/yyyy');
        this.passedInSearch = true;
        this.searchType = 'lastAction';
        const saved = `{"search": [{"searchBy": "action"}, {"userid": "${this.currentUser}"}, {"actionId": "2300,2310,2320,2330,2335,2390"}, {"actionoccurred": "${date}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
        const searchStorage = { type: this.searchType, searchDef: saved };

        this.ss.set('searchStorage', searchStorage);
        this.search(saved);
      } else if (pr && pr.type && pr.type === 'callList') {
        this.passedInSearch = true;
        this.searchType = pr.type;
        const saved = `{"search": [{"userid": "${this.currentUser}"}, {"searchby": "callList"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
        const searchStorage = { type: this.searchType, searchDef: saved };

        this.ss.set('searchStorage', searchStorage);
        this.search(saved);
      } else if (pr && pr.type && pr.type === 'timeSensitive') {
        this.passedInSearch = true;
        this.searchType = pr.type;
        const saved = `{"search": [{"searchby": "TimeSensitive"}, {"userid": "${this.currentUser}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
        const searchStorage = { type: this.searchType, searchDef: saved };

        this.ss.set('searchStorage', searchStorage);
        this.search(saved);
      } else if (pr && pr.type) {
        this.setSearchType(pr.type);
      }
    });
  }

  clearSearch() {
    this.ogData.data = [];
    this.ogData.total = 0;
    this.gridColumns = null;
    this.searchType = null;
    this.hideTiles = false;
    this.clearSavedSearch();
    this.router.navigate(['/se/search']);
  }

  clearSavedSearch() {
    this.ss.remove('searchStorage');
    this.savedSearch = null;
  }

  reloadSearch() {
    this.hideTiles = true;
    this.searchType = this.savedSearch.type;

    if (this.savedSearch.hierarchyData) {
      this.selectedUnit = this.savedSearch.hierarchyData.unit;
      this.selectedUser = this.savedSearch.hierarchyData.user;
      this.selectedRepCode = this.savedSearch.hierarchyData.rep;
    }

    this.search(this.savedSearch.searchDef, false, this.savedSearch.type);
  }

  setSearchType(type?, id?) {
    if (this.passedInSearch) {
      this.clearParams();
    }
    this.hideTiles = true;
    if (!type.value) {
      this.searchType = type;
    }

    if (this.searchType === 'highScore' || this.searchType === 'callList') {
      this.search(null, true);
    } else {
      const min = this.searchType === 'suggestion' ? '95vw' : '0vw';
      const saved = this.ss.get('searchStorage');
      if (saved && saved.type === this.searchType) {
        this.searchModel = saved;
      } else {
        this.searchModel = null;
      }
      const dialogRef = this.dialog.open(SearchModalComponent, {
        maxWidth: '95vw',
        minWidth: min,
        panelClass: 'search-dialog',
        data: { type: this.searchType, saved: this.searchModel },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result && result.data !== 'cancel') {
          this.searchModel = result.data;
          this.selectedUnit = this.searchModel.hierarchyData
            ? this.searchModel.hierarchyData.unit
            : null;
          this.selectedUser = this.searchModel.hierarchyData
            ? this.searchModel.hierarchyData.user
            : null;
          this.selectedRepCode = this.searchModel.hierarchyData
            ? this.searchModel.hierarchyData.rep
            : null;
          this.search(null, true);
        } else {
          this.searchType = this.savedSearch ? this.savedSearch.type : null;
          if (!this.searchType) {
            this.clearSearch();
          }
        }
      });
    }
  }

  search(saved?, ignoreSaved = false, type?, hierarchy?: boolean) {
    this.loading = true;
    const srch = { unit: null, user: null, rep: null };
    let searchDef: string;

    let filter = '';

    if (this.selectedUnit) {
      srch['unit'] = this.selectedUnit.id;
    }
    if (this.selectedUser) {
      srch['user'] = this.selectedUser.id;
    }
    if (this.selectedRepCode) {
      srch['rep'] = this.selectedRepCode.repCode;
    }
    ignoreSaved =
      type && type !== this.searchType && !ignoreSaved ? true : ignoreSaved;

    let checkedSaved;
    if (saved && !ignoreSaved) {
      checkedSaved = saved;
    } else if (!ignoreSaved && this.savedSearch && this.savedSearch.searchDef) {
      checkedSaved = this.savedSearch.searchDef;
    } else {
      this.clearSavedSearch();
    }

    if (!checkedSaved) {
      switch (this.searchType) {
        case 'name':
          if (this.selectedRepCode) {
            searchDef = `{"search": [${
              this.searchModel.firstName
                ? '{"firstName": "' + this.searchModel.firstName + '"},'
                : ''
            } ${
              this.searchModel.lastName
                ? '{"lastName": "' + this.searchModel.lastName + '"},'
                : ''
            } {"searchby": "name"}, {"repCode": "${
              srch['rep']
            }"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUser) {
            searchDef = `{"search": [${
              this.searchModel.firstName
                ? '{"firstName": "' + this.searchModel.firstName + '"},'
                : ''
            } ${
              this.searchModel.lastName
                ? '{"lastName": "' + this.searchModel.lastName + '"},'
                : ''
            } {"searchby": "name"}, {"userid": "${
              srch['user']
            }"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUnit) {
            searchDef = `{"search": [${
              this.searchModel.firstName
                ? '{"firstName": "' + this.searchModel.firstName + '"},'
                : ''
            } ${
              this.searchModel.lastName
                ? '{"lastName": "' + this.searchModel.lastName + '"},'
                : ''
            } {"searchby": "name"}, {"unitid": "${
              srch['unit']
            }"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else {
            searchDef = `{"search": [${
              this.searchModel.firstName
                ? '{"firstName": "' + this.searchModel.firstName + '"},'
                : ''
            } ${
              this.searchModel.lastName
                ? '{"lastName": "' + this.searchModel.lastName + '"},'
                : ''
            } {"searchby": "name"}, {"userid": "${
              this.currentUser
            }"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          }
          break;

        case 'opp':
          if (this.selectedRepCode) {
            searchDef = `{"search": [{"RelevantBridgeCount": ${this.searchModel.RelevantBridgeCount}}, {"searchby": "RelevantBridgeCount"}, {"repCode": "${srch['rep']}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUser) {
            searchDef = `{"search": [{"RelevantBridgeCount": ${this.searchModel.RelevantBridgeCount}}, {"searchby": "RelevantBridgeCount"}, {"userid": "${srch['user']}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUnit) {
            searchDef = `{"search": [{"RelevantBridgeCount": ${this.searchModel.RelevantBridgeCount}}, {"searchby": "RelevantBridgeCount"}, {"unitid": "${srch['unit']}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else {
            searchDef = `{"search": [{"RelevantBridgeCount": ${this.searchModel.RelevantBridgeCount}}, {"searchby": "RelevantBridgeCount"}, {"userid": "${this.currentUser}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          }
          break;

        case 'timeSensitive':
          if (this.searchModel.prospects) {
            filter = '{Prospects: true},';
          } else if (this.searchModel.prospects === false) {
            filter = '{Prospects: false},';
          }

          if (this.selectedRepCode) {
            searchDef = `{"search": [${filter} {"searchby": "TimeSensitive"}, {"repCode": "${srch['rep']}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUser) {
            searchDef = `{"search": [${filter} {"searchby": "TimeSensitive"}, {"userid": "${srch['user']}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUnit) {
            searchDef = `{"search": [${filter} {"searchby": "TimeSensitive"}, {"unitid": "${srch['unit']}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else {
            searchDef = `{"search": [${filter} {"searchby": "TimeSensitive"}, {"userid": "${this.currentUser}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          }
          break;

        case 'highScore':
          if (this.selectedRepCode) {
            searchDef = `{"search": [{"repcode":"${srch['rep']}"}, {"searchby":"highbridge"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUser) {
            searchDef = `{"search": [{"userid":"${srch['user']}"}, {"searchby":"highbridge"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUnit) {
            searchDef = `{"search": [{"unitid":"${srch['unit']}"}, {"searchby":"highbridge"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else {
            searchDef = `{"search": [{"userid":"${this.currentUser}"}, {"searchby":"highbridge"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          }
          break;

        case 'callList':
          if (this.selectedRepCode) {
            searchDef = `{"search": [{"repCode": "${srch['rep']}"}, {"searchby": "callList"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUser) {
            searchDef = `{"search": [{"userid": "${srch['user']}"}, {"searchby": "callList"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUnit) {
            searchDef = `{"search": [{"unitid": "${srch['user']}"}, {"searchby": "callList"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else {
            searchDef = `{"search": [{"userid": "${this.currentUser}"}, {"searchby": "callList"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          }
          break;

        case 'callBack':
          if (this.searchModel.prospects) {
            filter = '{Prospects: true},';
          } else if (this.searchModel.prospects === false) {
            filter = '{Prospects: false},';
          }

          if (this.selectedRepCode) {
            searchDef = `{"search": [${filter} {"repCode": "${srch['rep']}"}, {"searchby": "callBackDate"},  {"StartDate": "${this.searchModel.StartDate}"}, {"EndDate": "${this.searchModel.EndDate}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUser) {
            searchDef = `{"search": [${filter} {"userid": "${srch['user']}"}, {"searchby": "callBackDate"},  {"StartDate": "${this.searchModel.StartDate}"}, {"EndDate": "${this.searchModel.EndDate}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUnit) {
            searchDef = `{"search": [${filter} {"unitid": "${srch['user']}"}, {"searchby": "callBackDate"},  {"StartDate": "${this.searchModel.StartDate}"}, {"EndDate": "${this.searchModel.EndDate}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else {
            searchDef = `{"search": [${filter} {"userid": "${this.currentUser}"}, {"searchby": "callBackDate"},  {"StartDate": "${this.searchModel.StartDate}"}, {"EndDate": "${this.searchModel.EndDate}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          }
          break;

        case 'suggestion':
          if (this.selectedRepCode) {
            searchDef = `{"search": [{"searchBy": "profileBridge"}, {"repCode": "${srch['rep']}"}, {"bridgeids": "${this.searchModel.bridgeids}"}, {"score": ${this.searchModel.relThresh}}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUser) {
            searchDef = `{"search": [{"searchBy": "profileBridge"}, {"userid": "${srch['user']}"}, {"bridgeids": "${this.searchModel.bridgeids}"}, {"score": ${this.searchModel.relThresh}}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUnit) {
            searchDef = `{"search": [{"searchBy": "profileBridge"}, {"unitid": "${srch['unit']}"}, {"bridgeids": "${this.searchModel.bridgeids}"}, {"score": ${this.searchModel.relThresh}}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else {
            searchDef = `{"search": [{"searchBy": "profileBridge"}, {"userid": "${this.currentUser}"}, {"bridgeids": "${this.searchModel.bridgeids}"}, {"score": ${this.searchModel.relThresh}}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          }
          break;

        case 'lastAction':
          if (this.selectedRepCode) {
            searchDef = `{"search": [{"searchBy": "action"}, {"repCode": "${srch['rep']}"}, {"actionId": "${this.searchModel.actionids}"}, {"actionoccurred": "${this.searchModel.actionStartDate}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUser) {
            searchDef = `{"search": [{"searchBy": "action"}, {"userid": "${srch['user']}"}, {"actionId": "${this.searchModel.actionids}"}, {"actionoccurred": "${this.searchModel.actionStartDate}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUnit) {
            searchDef = `{"search": [{"searchBy": "action"}, {"unitid": "${srch['unit']}"}, {"actionId": "${this.searchModel.actionids}"}, {"actionoccurred": "${this.searchModel.actionStartDate}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else {
            searchDef = `{"search": [{"searchBy": "action"}, {"userid": "${this.currentUser}"}, {"actionId": "${this.searchModel.actionids}"}, {"actionoccurred": "${this.searchModel.actionStartDate}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          }
          break;

        case 'demographics':
          if (this.searchModel.additional === 'prospects') {
            filter = '{Prospects: true},';
          } else if (this.searchModel.additional === 'clients') {
            filter = '{Prospects: false},';
          }

          if (this.selectedRepCode) {
            searchDef = `{"search": [${filter} {"searchby": ""}, {"repCode": "${srch['rep']}"}, { "AgeLow": "${this.searchModel.ageFrom}"}, {"AgeHigh": "${this.searchModel.ageTo}"}, {"ProductsLow": "${this.searchModel.productsFrom}"}, {"ProductsHigh": "${this.searchModel.productsTo}"}, {"IncomeLow": "${this.searchModel.incomeFrom}"}, {"IncomeHigh": "${this.searchModel.incomeTo}"}, {"TotalLifeLow": "${this.searchModel.assetsFrom}"}, {"TotalLifeHigh": "${this.searchModel.assetsTo}"}, {"ClientScoreLow": "${this.searchModel.profileFrom}"}, {"ClientScoreHigh": "${this.searchModel.profileTo}"}, {"City": "${this.searchModel.city}"}, {"ProspectSource": "${this.searchModel.source}"}, {"PostalCodes": "${this.searchModel.postalCode}"}, {"MarketCategories": "${this.searchModel.marketCategory}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUser) {
            searchDef = `{"search": [${filter} {"searchby": ""}, {"userid": "${srch['user']}"}, { "AgeLow": "${this.searchModel.ageFrom}"}, {"AgeHigh": "${this.searchModel.ageTo}"}, {"ProductsLow": "${this.searchModel.productsFrom}"}, {"ProductsHigh": "${this.searchModel.productsTo}"}, {"IncomeLow": "${this.searchModel.incomeFrom}"}, {"IncomeHigh": "${this.searchModel.incomeTo}"}, {"TotalLifeLow": "${this.searchModel.assetsFrom}"}, {"TotalLifeHigh": "${this.searchModel.assetsTo}"}, {"ClientScoreLow": "${this.searchModel.profileFrom}"}, {"ClientScoreHigh": "${this.searchModel.profileTo}"}, {"City": "${this.searchModel.city}"}, {"ProspectSource": "${this.searchModel.source}"}, {"PostalCodes": "${this.searchModel.postalCode}"}, {"MarketCategories": "${this.searchModel.marketCategory}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else if (this.selectedUnit) {
            searchDef = `{"search": [${filter} {"searchby": ""}, {"unitid": "${srch['unit']}"}, { "AgeLow": "${this.searchModel.ageFrom}"}, {"AgeHigh": "${this.searchModel.ageTo}"}, {"ProductsLow": "${this.searchModel.productsFrom}"}, {"ProductsHigh": "${this.searchModel.productsTo}"}, {"IncomeLow": "${this.searchModel.incomeFrom}"}, {"IncomeHigh": "${this.searchModel.incomeTo}"}, {"TotalLifeLow": "${this.searchModel.assetsFrom}"}, {"TotalLifeHigh": "${this.searchModel.assetsTo}"}, {"ClientScoreLow": "${this.searchModel.profileFrom}"}, {"ClientScoreHigh": "${this.searchModel.profileTo}"}, {"City": "${this.searchModel.city}"}, {"ProspectSource": "${this.searchModel.source}"}, {"PostalCodes": "${this.searchModel.postalCode}"}, {"MarketCategories": "${this.searchModel.marketCategory}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          } else {
            searchDef = `{"search": [${filter} {"searchby": ""}, {"userid": "${this.currentUser}"}, { "AgeLow": "${this.searchModel.ageFrom}"}, {"AgeHigh": "${this.searchModel.ageTo}"}, {"ProductsLow": "${this.searchModel.productsFrom}"}, {"ProductsHigh": "${this.searchModel.productsTo}"}, {"IncomeLow": "${this.searchModel.incomeFrom}"}, {"IncomeHigh": "${this.searchModel.incomeTo}"}, {"TotalLifeLow": "${this.searchModel.assetsFrom}"}, {"TotalLifeHigh": "${this.searchModel.assetsTo}"}, {"ClientScoreLow": "${this.searchModel.profileFrom}"}, {"ClientScoreHigh": "${this.searchModel.profileTo}"}, {"City": "${this.searchModel.city}"}, {"ProspectSource": "${this.searchModel.source}"}, {"PostalCodes": "${this.searchModel.postalCode}"}, {"MarketCategories": "${this.searchModel.marketCategory}"}, {"startRow": ${this.startRow}}, {"endRow": ${this.endRow}}]}`;
          }
          break;

        default:
          break;
      }
    } else {
      searchDef = checkedSaved;
      if (!hierarchy) {
        this.selectedUnit = this.savedSearch?.hierarchyData
          ? this.savedSearch.hierarchyData.unit
          : null;
        this.selectedUser = this.savedSearch?.hierarchyData
          ? this.savedSearch.hierarchyData.user
          : null;
        this.selectedRepCode = this.savedSearch?.hierarchyData
          ? this.savedSearch.hierarchyData.rep
          : null;
      }
    }

    this.usService.profileSearch(searchDef).subscribe(x => {
      const data = x;
      this.ogData.data = data['data'];
      this.ogData.total = data['total'];
      this.hideTiles = true;
      const searchStorage = {
        type: this.searchType,
        searchDef: searchDef,
        hierarchyData: {
          unit: this.selectedUnit,
          user: this.selectedUser,
          rep: this.selectedRepCode,
        },
      };

      this.ss.set('searchStorage', searchStorage);
      this.savedSearch = searchStorage;

      if (data['headers'].length > 0) {
        data['headers'].forEach(z => {
          switch (z.RespVis) {
            case 'xs':
              z.RespVis = '';
              break;
            case 'sm':
              z.RespVis = '(min-width: 700px)';
              break;
            case 'md':
              z.RespVis = '(min-width: 1100px)';
              break;
            case 'lg':
              z.RespVis = '(min-width: 1200px)';
              break;
            case 'xl':
              z.RespVis = '(min-width: 1500px)';
              break;
          }

          if (z.Hidden) {
            this.hidden.push(z.DataField);
          }
        });

        this.ogData.data.forEach(el => {
          el.slctd = false;
        });

        this.gridColumns = data['headers'].filter(col => {
          const notLegit =
            col.DataField === 'id' ||
            col.DataField === 'IsInCallList' ||
            col.DataField === 'IsProspect' ||
            col.DataField === 'followupflag';
          return !notLegit;
        });
      } else {
        console.warn(data, 'no data, do something');
      }

      this.loading = false;
    });
  }

  pageChange(ev) {}

  clear() {}

  clearParams() {
    this.router.navigate([], {
      queryParams: {
        type: null,
        id: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  isHidden(columnName: string): boolean {
    return this.hidden.includes(columnName);
  }

  setStorage() {
    this.ss.set('hiddenSearchColumns', this.hidden);
  }

  suggCheck(ev, val) {
    this.searchModel.suggestions[val] = !this.searchModel.suggestions[val];
  }

  actionCheck(ev, val) {
    this.searchModel.actions[val] = !this.searchModel.actions[val];
  }

  setHierarchy() {
    const dialogRef = this.dialog.open(HierarchyModalComponent, {
      panelClass: 'hierarchy-dialog',
      minHeight: '30vh',
      width: '70vw',
      data: {
        mode: 'SE',
        data: {
          unit: this.selectedUnit ? this.selectedUnit : null,
          user: this.selectedUser ? this.selectedUser : null,
          rep: this.selectedRepCode ? this.selectedRepCode : null,
        },
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.data !== 'cancel') {
        this.selectedUnit = result.data.unit || null;
        this.selectedUser = result.data.user || null;
        this.selectedRepCode = result.data.rep || null;
        if (this.searchType) {
          this.search(null, true, null, true);
        }
      }
    });
  }

  clearHierarchy() {
    this.selectedUnit = null;
    this.selectedUser = null;
    this.selectedRepCode = null;
    this.searchModel.hierarchyData = {
      rep: null,
      unit: null,
      user: null,
    };
    this.savedSearch.hierarchyData = {
      rep: null,
      unit: null,
      user: null,
    };
    const savedData = this.ss.get('searchStorage');
    const savedSearchDef = JSON.parse(savedData.searchDef);

    const plainSearch = savedSearchDef.search.filter(searchItem => {
      return !(
        Object.keys(searchItem)[0] === 'userid' ||
        Object.keys(searchItem)[0] === 'unitid' ||
        Object.keys(searchItem)[0] === 'repCode'
      );
    });
    savedData.hierarchyData = {
      rep: null,
      unit: null,
      user: null,
    };

    savedData.searchDef = `{"search": ${JSON.stringify(plainSearch)}}`;
    this.savedSearch.searchDef = savedData.searchDef;
    this.search(this.savedSearch.searchDef, false, this.savedSearch.type);
  }

  viewProfile(ev) {
    this.router.navigate(['/se/client/' + ev.selectedRows[0].dataItem.id]);
  }

  selectAll(ev) {
    if (ev.checked) {
      this.ogData.data.forEach(el => {
        el.slctd = true;
        this.selectedProfiles.push(el.id);
      });
    } else {
      this.ogData.data.forEach(el => {
        el.slctd = false;
        this.selectedProfiles = [];
      });
    }
  }

  selectRow(ev, row) {
    if (ev.checked) {
      this.selectedProfiles.push(row.id);
    } else {
      const idx = this.selectedProfiles.indexOf(row.id);
      this.selectedProfiles.splice(idx, 1);
    }
  }

  executeBulk() {
    const data = { records: [] };
    if (this.selectedProfiles.length > 0) {
      if (this.bulkAction === 'excel') {
        this.loading = true;
        const iosSafari =
          this.pltfrm.IOS ||
          (navigator?.['userAgentData']?.platform === 'macOS' &&
            navigator.maxTouchPoints > 1);

        const client = this.selectedProfiles.map(z => z);
        this.usService.excelAddress(client).subscribe(data => {
          this.loading = true;
          const blob = new Blob([data.body], {
            type: 'text/plain;charset=utf-8',
          });
          const date = new Date();
          const filename = `expanded-excel-data-reportdate-${
            date.getMonth() + 1
          }${date.getDate()}${date.getFullYear()}.csv`;

          let fileData;
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => {
            fileData = reader.result;
            saveAs(fileData, filename, {
              proxyURL: `${this.baseUrl}/util/proxy`,
              proxyTarget: '_self',
              forceProxy: iosSafari,
            });
          };
          this.search();
          this.loading = false;
        });
      } else if (this.bulkAction === 'clientReport') {
        const client = this.selectedProfiles.map(z => z);
        const dialogRef = this.dialog.open(SharedReportDialogComponent, {
          panelClass: 'report-dialog',
          data: { app: 'se', bulk: false },
        });
        dialogRef.afterClosed().subscribe(result => {
          if (result && result.data !== 'cancel') {
            this.loading = true;
            const iosSafari =
              this.pltfrm.IOS ||
              (navigator?.['userAgentData']?.platform === 'macOS' &&
                navigator.maxTouchPoints > 1);

            const options = { ...result.data };
            this.rbs.getClientReport(client, options).subscribe(x => {
              const ext = client.length > 1 ? '.zip' : '.pdf';
              const filename = result.data.filename
                ? result.data.filename + ext
                : 'RightBridge-client-reports' + ext;

              let fileData;
              const reader = new FileReader();
              reader.readAsDataURL(x);
              reader.onloadend = () => {
                fileData = reader.result;
                saveAs(fileData, filename, {
                  proxyURL: `${this.baseUrl}/util/proxy`,
                  proxyTarget: '_self',
                  forceProxy: iosSafari,
                });
              };
              this.search();
              this.loading = false;
            });
          }
        });
      } else if (this.bulkAction === 'addToList') {
        this.ses.addToCallList(this.selectedProfiles).subscribe(() => {
          this.loading = true;
          this.search();
        });
      } else if (this.bulkAction === 'removeFromList') {
        this.ses.removeFromCallList(this.selectedProfiles).subscribe(() => {
          this.loading = true;
          this.search();
        });
      } else {
        this.selectedProfiles.forEach(el => {
          const entry = {
            profileID: el,
            actionID: this.bulkAction,
            note: null,
            followUpDate: null,
          };
          data.records.push(entry);
        });
        this.ses.takeBulkAction(data).subscribe(() => {
          this.search();
        });
      }
    }
    this.bulkAction = null;
    this.selectedProfiles = [];
    this.allSelected = false;
  }

  exportExcelData(): ExcelExportData {
    const data = [];
    this.selectedProfiles.forEach(el => {
      const entry = this.ogData.data.find(x => el === x.id);
      data.push(entry);
    });
    const ids = data.map(x => x.id);
    this.ses.viewedProfile(ids).subscribe();
    const result: ExcelExportData = {
      data: process(data, { group: this.group, sort: this.sort }).data,
      group: this.group,
    };
    return result;
  }

  nav(location: string) {
    this.router.navigate([`/${location}`]);
  }
}
